/* eslint-disable max-lines */
/**
 * Progress Service
 *
 * Manages user learning progress, including banana points, coins, crowns, and game statistics.
 * Tracks user advancement through lessons and achievements.
 */

import { SERVICE_NAMES } from '../core/constants';
import {
  EquivalentUnitGroup,
  InternalSDKOperations,
  LanguageCode,
  LessonEndSignals,
} from '../types';
import { BaseService } from './base-service';
import { NetworkDetectionManager } from '../utils/network-detection-manager';

/**
 * Service class for managing user learning progress and achievements
 */
export class ProgressService extends BaseService {
  protected serviceName = SERVICE_NAMES.PROGRESS;
  /**
   * Creates a new ProgressService instance
   * @param internalOps - Internal SDK operations interface
   * @param networkManager - Optional shared network detection manager
   */
  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager,
  ) {
    super(internalOps, networkManager);
  }

  async updateCoins(
    targetLanguage: LanguageCode,
    totalCoins: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateCoins';
    const parameters = { targetLanguage, totalCoins };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateExtraContentEligibility(
    targetLanguage: LanguageCode,
    isExtraContentAvailable: boolean,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateExtraContentEligibility';
    const parameters = { targetLanguage, isExtraContentAvailable };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserBananaPoints(
    targetLanguage: LanguageCode,
    currentPoints: number,
    addedPoints: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserBananaPoints';
    const parameters = { targetLanguage, currentPoints, addedPoints };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserChatbot(
    targetLanguage: LanguageCode,
    chatbotId: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserChatbot';
    const parameters = { targetLanguage, chatbotId };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserAITutor(
    targetLanguage: LanguageCode,
    aiTutorId: number,
    options?: { completedAt?: number | string; crown?: number },
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserAITutor';
    const parameters = { targetLanguage, aiTutorId, ...options };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserCoins(
    targetLanguage: LanguageCode,
    currentCoins: number,
    addedCoins: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserCoins';
    const parameters = { targetLanguage, currentCoins, addedCoins };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserCrowns(
    targetLanguage: LanguageCode,
    unitId: number,
    lesson: number,
    wrongAnswers: number,
    isExam: boolean,
    isWritingGame: boolean,
    isUniqueCourse: boolean,
    nativeLanguage?: LanguageCode,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserCrowns';
    const parameters = {
      targetLanguage,
      unitId,
      lesson,
      wrongAnswers,
      isExam,
      isWritingGame,
      isUniqueCourse,
      nativeLanguage,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserLessonCrowns(
    targetLanguage: LanguageCode,
    unitId: number,
    lessonId: number,
    crowns: number,
    isUniqueCourse: boolean,
    nativeLanguage?: LanguageCode,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserLessonCrowns';
    const parameters = {
      targetLanguage,
      unitId,
      lessonId,
      crowns,
      isUniqueCourse,
      nativeLanguage,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateWeekNumber(
    targetLanguage: LanguageCode,
    weekNumber: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateWeekNumber';
    const parameters = { targetLanguage, weekNumber };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateWeeklyGraph(
    targetLanguage: LanguageCode,
    dayNumber: number,
    currentDayPoints: number,
    addedPoints: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateWeeklyGraph';
    const parameters = {
      targetLanguage,
      dayNumber,
      currentDayPoints,
      addedPoints,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async getUserLanguageProgress(
    targetLanguage: LanguageCode,
    version = 'v1',
  ): Promise<{
    bananas: number;
    goal: Record<string, unknown>;
    progress: Record<string, unknown>;
    uniqueProgress: Record<string, unknown>;
    progressChatbot: Record<string, unknown>;
    progressAITutor: Record<string, unknown>;
    equivalentUnitGroups?: EquivalentUnitGroup[];
  }> {
    const isV2 = version === 'v2';
    const response = await this.internalOps.makeRequest<{
      bananas: number;
      goal: Record<string, unknown>;
      progress: Record<string, unknown>;
      uniqueProgress: Record<string, unknown>;
      progressChatbot: Record<string, unknown>;
      progressAITutor: Record<string, unknown>;
      equivalentUnitGroups?: EquivalentUnitGroup[];
    }>(
      SERVICE_NAMES.PROGRESS,
      'getUserLanguageProgress',
      isV2 ? {} : { targetLanguage },
      version,
      isV2 ? { method: 'GET', queryParams: { targetLanguage } } : undefined,
    );

    return response.data;
  }

  /**
   * Batch update all progress-related data in a single atomic operation
   * This consolidates multiple individual updates into one database write
   */
  async batchUpdateProgressData(
    targetLanguage: LanguageCode,
    progressData: {
      lessonCrowns?: {
        unitId: number;
        lessonId: number;
        crowns: number;
        isUniqueCourse: boolean;
        nativeLanguage?: LanguageCode;
      };
      bananaPoints: {
        currentPoints: number;
        addedPoints: number;
      };
      weekNumber: number;
      weeklyGraph: {
        dayNumber: number;
        currentDayPoints: number;
        addedPoints: number;
      };
      coins: {
        currentCoins: number;
        addedCoins: number;
      };
      chatbotId?: number; // Optional chatbot progress update
      progressAITutor?: Record<
        string,
        { completed: boolean; completedAt?: number | string; crown?: number }
      >; // Optional AI tutor progress update
    },
    lessonSignals?: LessonEndSignals,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'batchUpdateProgressData';
    const parameters: {
      targetLanguage: LanguageCode;
      progressData: typeof progressData;
      lessonSignals?: LessonEndSignals;
    } = { targetLanguage, progressData };

    if (lessonSignals != null) {
      parameters.lessonSignals = lessonSignals;
    }

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }

  /**
   * Copy a user's lesson-crown maps from old unit IDs onto one or more new unit IDs
   * within `progress`. Atomic on the server. Used by the units 3-10 curriculum
   * remap migration.
   */
  async bulkCopyUnits(
    targetLanguage: LanguageCode,
    mappings: { from: string; to: string[] }[],
    version = 'v1',
  ): Promise<void> {
    const functionName = 'bulkCopyUnits';
    const parameters = { targetLanguage, mappings };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<unknown>(
        SERVICE_NAMES.PROGRESS,
        functionName,
        parameters,
        version,
      ),
    );
  }
}
